import { createRouter, createWebHistory } from 'vue-router';
import MessageHub from '~/components/message-hub/MessageHub.vue';
import MessageHubBulkActions from '~/components/message-hub/message-hub-bulk-actions/MessageHubBulkActions.vue';
import MessageHubContactView from '~/components/message-hub/MessageHubContactView.vue';
import MessageHubConversation from '~/components/message-hub/MessageHubConversation.vue';
import MessageHubNewConversation from '~/components/message-hub/MessageHubNewConversation.vue';
import MessageHubScheduledMessages from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessages.vue';
import MessageHubScheduledMessage from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessage.vue';
import MessageHubNoThreadSelected from '~/components/message-hub/message-hub-threads/MessageHubNoThreadSelected.vue';
import { canUseMessageHub } from './components/message-hub/messageHubUtil';
import CampaignList from '~/components/campaigns/list/CampaignList.vue';
import CampaignStepForm from '~/components/campaigns/campaign-step-form/CampaignStepForm.vue';
import CampaignContacts from '~/components/campaigns/contacts/CampaignContacts.vue';
import CampaignDetails from '~/components/campaigns/CampaignDetails.vue';
import { useCurrentGroup } from './composables/useCurrentGroup';
import GroupQuickBlastEditForm from '~/components/quick-blast/edit/groups/GroupQuickBlastEditForm.vue';
import { quickBlastRouteProps } from '~/components/quick-blast/quickBlastRouteProps';
import qs from 'qs';
import { hasScope } from '~/utils/users';

const QuickBlastPage = () => import('~/components/quick-blast/QuickBlastPage.vue');

const SnippetList = () => import('~/components/snippets/SnippetList.vue');
const SnippetTemplateForm = () => import('~/components/snippets/SnippetTemplateForm.vue');

const ReservedDatesList = () => import('~/components/reserved-dates/ReservedDatesList.vue');
const ReservedDateForm = () => import('~/components/reserved-dates/ReservedDateForm.vue');
const ReservedDateDetails = () => import('~/components/reserved-dates/ReservedDateDetails.vue');

const ContactList = () => import('~/components/contacts/contact/ContactList.vue');
const ContactForm = () => import('~/components/contacts/contact/form/ContactForm.vue');
const ContactDetails = () => import('~/components/contacts/contact/ContactDetails.vue');
const ContactView = () => import('~/components/contacts/contact/ContactView.vue');
const ContactMessages = () => import('~/components/contacts/contact/messages/ContactMessages.vue');
const ContactSubscriptions = () => import('~/components/contacts/contact/subscriptions/ContactSubscriptions.vue');
const ContactCustomAttributes = () =>
    import('~/components/contacts/contact/custom-attributes/ContactCustomAttributes.vue');
const ContactCampaigns = () => import('~/components/contacts/contact/campaigns/ContactCampaigns.vue');
const ContactPromotions = () => import('~/components/contacts/contact/promotions/ContactPromotions.vue');
const ContactSegments = () => import('~/components/contacts/contact/segments/ContactSegments.vue');
const ContactsImport = () => import('~/components/contacts/import/ContactsImport.vue');
const ContactImportNew = () => import('~/components/contacts/import/new/ContactImportNew.vue');
const ContactExport = () => import('~/components/contacts/export/ContactExport.vue');
const ContactManageTags = () => import('~/components/contacts/tags/ContactManageTags.vue');
const CustomAttributesList = () => import('~/components/contacts/tags/CustomAttributesList.vue');
const ExternalListList = () => import('~/components/contacts/external-lists/ExternalListList.vue');
const ExternalListAccessEdit = () => import('~/pages/ExternalListAccessEdit.vue');
const SegmentList = () => import('~/components/contacts/segments/SegmentList.vue');
const SegmentStepForm = () => import('~/components/contacts/segments/segment-step-form/SegmentStepForm.vue');
const SegmentContacts = () => import('~/components/contacts/segments/contacts/SegmentContacts.vue');

const AutomationRuleList = () => import('~/components/automation/AutomationRuleList.vue');
const AutomationRuleForm = () => import('~/components/automation/AutomationRuleForm.vue');

const FlowsList = () => import('~/components/automation/flows/FlowsList.vue');
const FlowForm = () => import('~/components/automation/flows/FlowForm.vue');
const FlowDetailForm = () => import('~/components/automation/flows/flow/FlowDetailForm.vue');
const FlowResources = () => import('~/components/automation/flows/flow/FlowResources.vue');

const WorkflowBuilderLayout = () => import('./components/workflow-builder/WorkflowBuilderLayout.vue');
const WorkflowBuilder = () => import('./components/workflow-builder/pages/WorkflowBuilder.vue');
const WorkflowBuilderAnalytics = () => import('./components/workflow-builder/pages/WorkflowBuilderAnalytics.vue');
const WorkflowVersionAnalytics = () => import('./components/workflow-builder/pages/WorkflowVersionAnalytics.vue');
const WorkflowBuilderArchived = () => import('./components/workflow-builder/pages/WorkflowBuilderArchived.vue');
const WorkflowEditor = () => import('./components/workflow-builder/pages/WorkflowEditor.vue');

const HomePage = () => import('~/components/home/HomePage.vue');

const AnalyticsDashboard = () => import('~/components/analytics/AnalyticsDashboard.vue');
const AnalyticsContacts = () => import('~/components/analytics/AnalyticsContacts.vue');
const AnalyticsEntryPoints = () => import('~/components/analytics/AnalyticsEntryPoints.vue');
const AnalyticsOutboundMessages = () => import('~/components/analytics/AnalyticsOutboundMessages.vue');
const AnalyticsBlastForecast = () => import('~/components/analytics/blast-forecast/AnalyticsBlastForecast.vue');

const { onCorporate, currentOrgUnit, currentGroup, roleOnCurrentOrgUnit, roleOnCurrentGroup } = useCurrentGroup();
import { FEATURES } from '~/provides/features';

const onOrgUnit = () => Boolean(currentOrgUnit.value?.id);
const onGroup = () => Boolean(currentGroup.value?.group_id);

const allowOrRedirectHome = (allowed) => {
    if (allowed) {
        return true;
    }

    return { name: 'home' };
};

const roleOnCurrentContext = () => (onOrgUnit() ? roleOnCurrentOrgUnit : roleOnCurrentGroup);

const canViewContextAnalytics = () => {
    const role = roleOnCurrentContext();

    return Boolean(role) && 'agent' !== role;
};

const canViewAnalytics = () => {
    if (!hasScope('analytics:read')) {
        return false;
    }

    if (!FEATURES.contact_groups || onCorporate()) {
        return true;
    }

    return canViewContextAnalytics();
};

const canViewCorporateAnalytics = () => onCorporate() && hasScope('analytics:read');

const canViewAffiliatedContactsAnalytics = () => FEATURES.contact_groups && onGroup() && 'agent' !== roleOnCurrentGroup;

const canViewPreferredContactsAnalytics = () => FEATURES.contact_groups && !onCorporate() && canViewContextAnalytics();

const canViewBlastForecast = () => !onGroup() && canViewAnalytics();

const analyticsContextProps = () => {
    if (!FEATURES.contact_groups) {
        return {};
    }

    if (onOrgUnit()) {
        return { orgUnitId: currentOrgUnit.value.id };
    }

    if (onGroup()) {
        return { groupId: currentGroup.value.group_id };
    }

    return {};
};

const redirectHomeUnlessCorporate = () => {
    if (onCorporate()) {
        return;
    }

    return { name: 'home' };
};

const redirectHomeOnOrgUnit = () => {
    if (!currentOrgUnit.value?.id) {
        return;
    }

    return { name: 'home' };
};

const ORG_UNIT_SEGMENT_EDITOR_ROLES = ['admin', 'marketer'];
const GROUP_SEGMENT_EDITOR_ROLES = ['owner', 'admin', 'marketer'];

const segmentScope = () => {
    if (FEATURES.contact_groups && currentOrgUnit.value?.id) {
        return {
            orgUnitId: currentOrgUnit.value.id,
            canEditSegments: ORG_UNIT_SEGMENT_EDITOR_ROLES.includes(roleOnCurrentOrgUnit),
        };
    }

    if (FEATURES.contact_groups && currentGroup.value?.group_id) {
        return {
            groupId: currentGroup.value.group_id,
            canEditSegments: GROUP_SEGMENT_EDITOR_ROLES.includes(roleOnCurrentGroup),
        };
    }

    return { canEditSegments: true };
};

const segmentListProps = () => {
    const { orgUnitId, groupId, canEditSegments } = segmentScope();

    return { orgUnitId, groupId, readOnly: !canEditSegments, templateView: false };
};

const segmentFormProps = () => {
    const { orgUnitId, groupId } = segmentScope();

    return { orgUnitId, groupId, templateView: false };
};

const redirectToSegmentsUnlessEditor = () => {
    if (segmentScope().canEditSegments) {
        return;
    }

    return { name: 'segments' };
};

const snippetListProps = () => {
    if (!FEATURES.contact_groups) {
        return { templateView: false };
    }

    return {
        templateView: false,
        orgUnitId: currentOrgUnit.value?.id,
        groupId: currentGroup.value?.group_id,
    };
};

const paths = [
    {
        path: '/',
        name: 'home',
        component: HomePage,
    },
    {
        path: '/home',
        redirect: { name: 'home' },
    },
    {
        path: '/workflow-builder',
        component: WorkflowBuilderLayout,
        meta: { title: 'Workflow Builder', hideAppNavbar: true },
        beforeEnter: () => {
            if (!FEATURES.workflow_builder || !onCorporate()) {
                return { name: 'home' };
            }

            return true;
        },
        children: [
            {
                path: '',
                name: 'workflow-builder',
                meta: { noContainerSpacing: true },
                component: WorkflowBuilder,
            },
            {
                path: 'analytics',
                name: 'workflow-builder.analytics',
                meta: { title: 'Analytics · Workflow Builder' },
                component: WorkflowBuilderAnalytics,
            },
            {
                path: 'analytics/:workflowId/versions/:version',
                name: 'workflow-builder.analytics.version',
                meta: { title: 'Version Analytics · Workflow Builder' },
                component: WorkflowVersionAnalytics,
            },
            {
                path: 'archived',
                name: 'workflow-builder.archived',
                meta: { title: 'Archived · Workflow Builder' },
                component: WorkflowBuilderArchived,
            },
            {
                path: ':workflowId',
                name: 'workflow-builder.edit',
                meta: { noContainerSpacing: true, hideNav: true },
                component: WorkflowEditor,
            },
        ],
    },
    {
        path: '/message-hub',
        component: MessageHub,
        meta: { title: 'Message Hub' },
        beforeEnter: () => {
            if (!canUseMessageHub()) {
                return { name: 'home' };
            }

            return true;
        },
        children: [
            {
                path: '',
                name: 'message-hub',
                component: MessageHubNoThreadSelected,
            },
            {
                path: 'new-message',
                name: 'message-hub.new-message',
                component: MessageHubNewConversation,
            },
            {
                path: 'bulk-actions',
                name: 'message-hub.bulk-actions',
                component: MessageHubBulkActions,
            },
            {
                path: 'scheduled',
                name: 'message-hub.scheduled-messages',
                component: MessageHubScheduledMessages,
            },
            {
                path: 'scheduled/:scheduledMessageId',
                name: 'message-hub.scheduled-message',
                component: MessageHubScheduledMessage,
            },
            {
                path: ':contactId',
                name: 'message-hub.conversation',
                component: MessageHubConversation,
                children: [
                    {
                        path: 'view',
                        name: 'message-hub.contact-view',
                        component: MessageHubContactView,
                    },
                ],
            },
            // Add 404 catch-all route
            {
                path: ':pathMatch(.*)*',
                name: 'not-found',
                component: MessageHubConversation,
            },
        ],
    },
    {
        path: '/messaging',
        children: [
            {
                path: 'quick-blast',
                children: [
                    {
                        path: '',
                        name: 'quick-blast',
                        component: QuickBlastPage,
                        meta: { title: 'Create Quick Blast' },
                        props: (route) => quickBlastRouteProps(route.query),
                    },
                    {
                        path: 'new',
                        component: GroupQuickBlastEditForm,
                        name: 'quick-blast.new',
                        meta: { title: 'Create Quick Blast' },
                        beforeEnter: (to) => {
                            if (!to.query.duplicate_from_id || onCorporate()) {
                                return { name: 'quick-blast' };
                            }

                            return true;
                        },
                    },
                    {
                        path: ':id(\\d+)',
                        component: GroupQuickBlastEditForm,
                        name: 'quick-blast.edit',
                        meta: { title: 'Campaign Details' },
                        props: (route) => ({ campaignId: Number(route.params.id) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return { name: 'campaigns' };
                            }
                        },
                    },
                ],
            },
            {
                path: 'snippets',
                children: [
                    {
                        path: '',
                        name: 'snippets',
                        component: SnippetList,
                        meta: { title: 'Snippets' },
                        props: snippetListProps,
                    },
                    {
                        path: 'templates',
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }

                            return { name: 'snippets' };
                        },
                        children: [
                            {
                                path: '',
                                name: 'snippets.templates',
                                component: SnippetList,
                                meta: { title: 'Snippet Templates' },
                                props: { templateView: true },
                            },
                            {
                                path: 'new',
                                name: 'snippets.templates.new',
                                component: SnippetTemplateForm,
                                meta: { title: 'Create · Snippet Templates' },
                            },
                            {
                                path: ':snippetId(\\d+)',
                                name: 'snippets.templates.edit',
                                component: SnippetTemplateForm,
                                meta: { title: 'Snippet Templates' },
                                props: (route) => ({ snippetId: Number(route.params.snippetId) }),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'campaigns',
                children: [
                    {
                        path: '',
                        name: 'campaigns',
                        component: CampaignList,
                        meta: { title: 'Campaigns' },
                    },
                    {
                        path: 'new',
                        component: CampaignDetails,
                        beforeEnter: (to) => {
                            if (onCorporate()) {
                                return;
                            }

                            if (to.query.duplicate_from_id) {
                                return {
                                    name: 'quick-blast.new',
                                    query: { duplicate_from_id: to.query.duplicate_from_id },
                                };
                            }

                            return { name: 'quick-blast' };
                        },
                        children: [
                            {
                                path: '',
                                name: 'campaigns.new',
                                component: CampaignStepForm,
                                meta: { title: 'Create Campaign' },
                            },
                        ],
                    },
                    {
                        path: ':id(\\d+)',
                        component: CampaignDetails,
                        props: (route) => ({ campaignId: Number(route.params.id) }),
                        children: [
                            {
                                path: '',
                                name: 'campaigns.edit',
                                component: CampaignStepForm,
                                meta: { title: 'Campaign Details' },
                                props: (route) => ({ campaignId: Number(route.params.id) }),
                                beforeEnter: (to) => {
                                    if (onCorporate()) {
                                        return;
                                    }

                                    return { name: 'quick-blast.edit', params: to.params, query: to.query };
                                },
                            },
                            {
                                path: 'contacts',
                                name: 'campaigns.contacts',
                                component: CampaignContacts,
                                meta: { title: 'Campaign Contacts' },
                                props: (route) => ({ campaignId: Number(route.params.id) }),
                                beforeEnter: () => {
                                    if (onCorporate()) {
                                        return;
                                    }

                                    return { name: 'campaigns' };
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'reserved-dates',
                beforeEnter: () => {
                    if (FEATURES.contact_groups && onCorporate()) {
                        return;
                    }

                    return { name: 'home' };
                },
                children: [
                    {
                        path: '',
                        name: 'reserved-dates',
                        component: ReservedDatesList,
                        meta: { title: 'Reserved Dates' },
                    },
                    {
                        path: 'new',
                        component: ReservedDateDetails,
                        children: [
                            {
                                path: '',
                                name: 'reserved-dates.new',
                                component: ReservedDateForm,
                                meta: { title: 'New Reserved Date' },
                            },
                        ],
                    },
                    {
                        path: ':id(\\d+)',
                        component: ReservedDateDetails,
                        props: (route) => ({ reservedDateId: Number(route.params.id) }),
                        children: [
                            {
                                path: '',
                                name: 'reserved-dates.edit',
                                component: ReservedDateForm,
                                meta: { title: 'Edit Reserved Date' },
                                props: (route) => ({ reservedDateId: Number(route.params.id) }),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'forecast',
                name: 'forecast',
                component: AnalyticsBlastForecast,
                meta: { title: 'Messaging · Blast Forecast' },
                props: analyticsContextProps,
                beforeEnter: () => allowOrRedirectHome(canViewBlastForecast()),
            },
        ],
    },
    {
        path: '/contacts',
        children: [
            {
                path: '',
                name: 'contacts.list',
                component: ContactList,
                meta: { title: 'Contacts' },
            },
            {
                path: 'new',
                component: ContactDetails,
                children: [
                    {
                        path: '',
                        name: 'contacts.new',
                        component: ContactForm,
                        meta: { title: 'New Contact' },
                    },
                ],
            },
            {
                path: ':contactId(\\d+)',
                component: ContactDetails,
                props: (route) => ({ contactId: Number(route.params.contactId) }),
                children: [
                    {
                        path: '',
                        name: 'contacts.edit',
                        component: ContactForm,
                        meta: { title: 'Edit Contact' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'view',
                        name: 'contacts.view',
                        component: ContactView,
                        meta: { title: 'Contact' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'messages',
                        name: 'contacts.messages',
                        component: ContactMessages,
                        meta: { title: 'Messages' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'subscriptions',
                        name: 'contacts.subscriptions',
                        component: ContactSubscriptions,
                        meta: { title: 'Subscriptions' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'custom-attributes',
                        name: 'contacts.custom-attributes',
                        component: ContactCustomAttributes,
                        meta: { title: 'Custom Attributes' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'campaigns',
                        name: 'contacts.campaigns',
                        component: ContactCampaigns,
                        meta: { title: 'Campaigns' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }
                            return { name: 'contacts.list' };
                        },
                    },
                    {
                        path: 'promotions',
                        name: 'contacts.promotions',
                        component: ContactPromotions,
                        meta: { title: 'Promotions' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }
                            return { name: 'contacts.list' };
                        },
                    },
                    {
                        path: 'segments',
                        name: 'contacts.segments',
                        component: ContactSegments,
                        meta: { title: 'Segments' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }
                            return { name: 'contacts.list' };
                        },
                    },
                ],
            },
            {
                path: 'import',
                beforeEnter: redirectHomeOnOrgUnit,
                children: [
                    {
                        path: '',
                        name: 'contacts.imports',
                        component: ContactsImport,
                        meta: { title: 'Imports' },
                    },
                    {
                        path: 'new',
                        name: 'contacts.imports.new',
                        component: ContactImportNew,
                        meta: { title: 'New Import' },
                    },
                ],
            },
            {
                path: 'export/new',
                name: 'contacts.export',
                component: ContactExport,
                meta: { title: 'Export' },
                beforeEnter: redirectHomeUnlessCorporate,
            },
            {
                path: 'tags',
                name: 'contacts.tags',
                component: ContactManageTags,
                meta: { title: 'Tags' },
                beforeEnter: redirectHomeUnlessCorporate,
            },
            {
                path: 'custom-attributes',
                name: 'custom-attributes',
                component: CustomAttributesList,
                meta: { title: 'Custom Attributes' },
                beforeEnter: redirectHomeUnlessCorporate,
            },
            {
                path: 'external-contact-lists',
                beforeEnter: [
                    redirectHomeUnlessCorporate,
                    () => {
                        if (FEATURES.external_contact_lists) {
                            return;
                        }
                        return { name: 'contacts.list' };
                    },
                ],
                children: [
                    {
                        path: '',
                        name: 'contacts.external-lists',
                        component: ExternalListList,
                        meta: { title: 'External Lists' },
                    },
                    {
                        path: ':externalListId(\\d+)/access',
                        name: 'contacts.external-lists.access',
                        component: ExternalListAccessEdit,
                        meta: { title: 'Access · External List' },
                        props: (route) => ({ externalListId: Number(route.params.externalListId) }),
                        beforeEnter: () => {
                            if (FEATURES.contact_groups) {
                                return;
                            }
                            return { name: 'contacts.external-lists' };
                        },
                    },
                ],
            },
            {
                path: 'segments',
                children: [
                    {
                        path: '',
                        name: 'segments',
                        component: SegmentList,
                        meta: { title: 'Segments' },
                        props: segmentListProps,
                    },
                    {
                        path: 'new',
                        name: 'segments.new',
                        component: SegmentStepForm,
                        meta: { title: 'Create · Segment' },
                        props: segmentFormProps,
                        beforeEnter: redirectToSegmentsUnlessEditor,
                    },
                    {
                        path: 'templates',
                        beforeEnter: redirectHomeUnlessCorporate,
                        children: [
                            {
                                path: '',
                                name: 'segments.templates',
                                component: SegmentList,
                                meta: { title: 'Segment Templates' },
                                props: { templateView: true },
                            },
                            {
                                path: 'new',
                                name: 'segments.templates.new',
                                component: SegmentStepForm,
                                meta: { title: 'Create · Segment Template' },
                                props: { templateView: true },
                            },
                            {
                                path: ':segmentId(\\d+)',
                                name: 'segments.templates.edit',
                                component: SegmentStepForm,
                                meta: { title: 'Segment' },
                                props: (route) => ({ templateView: true, segmentId: Number(route.params.segmentId) }),
                            },
                        ],
                    },
                    {
                        path: ':segmentId(\\d+)',
                        name: 'segments.edit',
                        component: SegmentStepForm,
                        meta: { title: 'Segment' },
                        props: (route) => ({ ...segmentFormProps(), segmentId: Number(route.params.segmentId) }),
                        beforeEnter: redirectToSegmentsUnlessEditor,
                    },
                    {
                        path: ':segmentId(\\d+)/contacts',
                        name: 'segments.contacts',
                        component: SegmentContacts,
                        meta: { title: 'Contacts · Segment' },
                        props: (route) => ({ segmentId: Number(route.params.segmentId) }),
                        beforeEnter: redirectHomeUnlessCorporate,
                    },
                ],
            },
        ],
    },
    {
        path: '/automation/event',
        children: [
            {
                path: '',
                name: 'automation.list',
                component: AutomationRuleList,
                meta: { title: 'Automations' },
            },
            {
                path: 'new',
                name: 'automation.new',
                component: AutomationRuleForm,
                meta: { title: 'Create Automation' },
            },
            {
                path: ':automationRuleId(\\d+)',
                name: 'automation.edit',
                component: AutomationRuleForm,
                meta: { title: 'Edit Automation' },
                props: (route) => ({ automationRuleId: Number(route.params.automationRuleId) }),
            },
        ],
    },
    {
        path: '/automation/flows',
        beforeEnter: () => {
            if (FEATURES.flows && onCorporate()) {
                return;
            }
            return { name: 'automation.list' };
        },
        children: [
            {
                path: '',
                name: 'flows.list',
                component: FlowsList,
                meta: { title: 'Flows' },
            },
            {
                path: 'new',
                component: FlowForm,
                children: [
                    {
                        path: '',
                        name: 'flows.new',
                        component: FlowDetailForm,
                        meta: { title: 'Create Flow' },
                    },
                ],
            },
            {
                path: ':flowId',
                component: FlowForm,
                props: (route) => ({ flowId: route.params.flowId }),
                children: [
                    {
                        path: '',
                        name: 'flows.edit',
                        component: FlowDetailForm,
                        meta: { title: 'Edit Flow' },
                    },
                    {
                        path: 'resources',
                        name: 'flows.resources',
                        component: FlowResources,
                        meta: { title: 'Flow Resources' },
                        props: (route) => ({ flowId: route.params.flowId }),
                    },
                ],
            },
        ],
    },
    {
        path: '/analytics',
        children: [
            {
                path: 'dashboard',
                name: 'analytics.dashboard',
                component: AnalyticsDashboard,
                meta: { title: 'Analytics · Dashboard' },
                props: analyticsContextProps,
                beforeEnter: () => allowOrRedirectHome(canViewAnalytics()),
            },
            {
                path: 'contacts',
                name: 'analytics.contacts',
                component: AnalyticsContacts,
                meta: { title: 'Analytics · Contacts' },
                beforeEnter: () => allowOrRedirectHome(canViewCorporateAnalytics()),
            },
            {
                path: 'affiliated-contacts',
                name: 'analytics.affiliated-contacts',
                component: AnalyticsContacts,
                meta: { title: 'Analytics · Affiliated Contacts' },
                props: () => ({ type: 'affiliated', ...analyticsContextProps() }),
                beforeEnter: () => allowOrRedirectHome(canViewAffiliatedContactsAnalytics()),
            },
            {
                path: 'preferred-contacts',
                name: 'analytics.preferred-contacts',
                component: AnalyticsContacts,
                meta: { title: 'Analytics · Preferred Contacts' },
                props: () => ({ type: 'preferred', ...analyticsContextProps() }),
                beforeEnter: () => allowOrRedirectHome(canViewPreferredContactsAnalytics()),
            },
            {
                path: 'entry-points',
                name: 'analytics.entry-points',
                component: AnalyticsEntryPoints,
                meta: { title: 'Analytics · Entry Point' },
                beforeEnter: () => allowOrRedirectHome(canViewCorporateAnalytics()),
            },
            {
                path: 'outbound-messages',
                name: 'analytics.outbound-messages',
                component: AnalyticsOutboundMessages,
                meta: { title: 'Analytics · Outbound Messages' },
                props: analyticsContextProps,
                beforeEnter: () => allowOrRedirectHome(canViewAnalytics()),
            },
        ],
    },
];

export const routes = createRouter({
    history: createWebHistory(),
    routes: paths,
    scrollBehavior(to, from) {
        if (to.hash) {
            return { el: to.hash, behavior: 'smooth' };
        }
        if (to.params.contactId && to.params.contactId === from.params.contactId) {
            return;
        }
        if (to.path !== from.path) {
            return { left: 0, top: 0 };
        }
    },
    stringifyQuery: (query) => {
        return qs.stringify(query, { arrayFormat: 'brackets', skipNulls: true });
    },
    parseQuery: (query) => {
        return qs.parse(query, { arrayFormat: 'brackets', skipNulls: true, ignoreQueryPrefix: true });
    },
});

const leavesRouterPages = (to, from) => to.matched.length === 0 && from.matched.length > 0;

routes.beforeEach((to, from) => {
    if (leavesRouterPages(to, from)) {
        window.location.assign(to.fullPath);
        return false;
    }
});

routes.afterEach((to) => {
    if (to.matched.length === 0) {
        return;
    }

    document.title = to.meta.title ? `${to.meta.title} · Voxie` : 'Voxie';
});
